import { z } from 'zod';

/** Limits of an audience definition (BB-40): nesting of logical nodes, number of filters, list and regex sizes. */
export const AUDIENCE_LIMITS = { maxDepth: 3, maxFilters: 50, maxValues: 256, maxRegexLength: 200 } as const;

/**
 * Operators per value type (SEG-002). Negative operators (`not_*`) are the exact negation of their positive pair, so a
 * missing or `null` value matches every negative operator and no positive one; `exists` / `not_exists` test presence.
 */
export const STRING_OPERATORS = ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'matches_regex', 'not_matches_regex', 'is_any_of', 'exists', 'not_exists'] as const;
export const NUMBER_OPERATORS = ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'between', 'exists', 'not_exists'] as const;
export const BOOLEAN_OPERATORS = ['is_true', 'is_false', 'exists', 'not_exists'] as const;
export const TIME_OPERATORS = ['before', 'after', 'on', 'more_than_days_ago', 'less_than_days_ago', 'within_next_days', 'exists', 'not_exists'] as const;
export const ARRAY_OPERATORS = ['includes_value', 'not_includes_value', 'size_equals', 'size_gt', 'size_lt', 'exists', 'not_exists'] as const;

export const FilterValueType = z.enum(['string', 'number', 'boolean', 'time', 'array']);
export type FilterValueType = z.infer<typeof FilterValueType>;

export const OPERATORS_BY_TYPE: Record<FilterValueType, readonly string[]> = {
  string: STRING_OPERATORS,
  number: NUMBER_OPERATORS,
  boolean: BOOLEAN_OPERATORS,
  time: TIME_OPERATORS,
  array: ARRAY_OPERATORS,
};

/** Operand of a comparison; its shape depends on the operator (checked by the engine with a path to the filter). */
export const Operand = z.union([
  z.string().max(1000),
  z.number(),
  z.boolean(),
  z.array(z.union([z.string().max(1000), z.number()])).max(AUDIENCE_LIMITS.maxValues),
  z.null(),
]);
export type Operand = z.infer<typeof Operand>;

export const Comparison = z.strictObject({
  operator: z.string().min(1).max(40),
  value: Operand.optional(),
  /** Second bound of `between`. */
  value2: z.number().optional(),
});

/** Standard profile fields a filter can read (more arrive with the BB-41 catalog). */
export const PROFILE_STRING_FIELDS = ['external_user_id', 'email', 'phone', 'first_name', 'last_name', 'language', 'country', 'timezone'] as const;
export const ProfileStringField = z.enum(PROFILE_STRING_FIELDS);
/** Fields of a profile's first paid AppsFlyer install (BB-131). */
export const INSTALL_ATTRIBUTION_FIELDS = ['source', 'source_kind', 'campaign', 'adgroup', 'ad'] as const;
export type ProfileStringField = z.infer<typeof ProfileStringField>;

const AppId = z.uuid();
export const DeviceScope = z.enum(['any_device', 'matching_devices_only']);
export const DevicePlatform = z.enum(['android', 'ios', 'web']);
export const PUSH_SUBSCRIPTION_STATES = ['opted_in', 'subscribed', 'unsubscribed'] as const;
export const VERSION_OPERATORS = ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'exists', 'not_exists'] as const;

const EventName = z.string().min(1).max(255);
export const EVENT_COUNT_OPERATORS = ['equals', 'gt', 'gte', 'lt', 'lte'] as const;
export const EVENT_TIME_OPERATORS = ['before', 'after', 'more_than_ago', 'less_than_ago'] as const;
/** `rolling`: the last N × 24 hours (exact, raw events); `calendar`: today and the N − 1 previous days in the environment timezone. */
export const EventWindow = z.strictObject({ days: z.number().int().min(1).max(3650), mode: z.enum(['rolling', 'calendar']) });
export type EventWindow = z.infer<typeof EventWindow>;
/** Default lookback of raw-event filters (rolling windows, property filters); `environment.settings.eventPropertyLookbackDays`. */
export const DEFAULT_EVENT_LOOKBACK_DAYS = 90;

/** What a retargeting filter looks at (BB-43): a campaign, a Canvas, one Canvas step, or every campaign/Canvas with a tag. */
export const MessageTarget = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('campaign'), id: z.uuid() }),
  z.strictObject({ kind: z.literal('canvas'), id: z.uuid() }),
  z.strictObject({ kind: z.literal('canvas_step'), id: z.uuid() }),
  z.strictObject({ kind: z.literal('tag'), id: z.uuid() }),
]);
export type MessageTarget = z.infer<typeof MessageTarget>;
export const MESSAGE_CHANNELS = ['push', 'email', 'sms', 'content_card', 'in_app', 'webhook'] as const;
const CampaignOrCanvas = z.discriminatedUnion('kind', [z.strictObject({ kind: z.literal('campaign'), id: z.uuid() }), z.strictObject({ kind: z.literal('canvas'), id: z.uuid() })]);

/**
 * One condition; `type` selects the filter module of the registry. BB-40 has the engine, BB-41 the MVP catalogue
 * (`GET …/audience/filter-catalog` describes every type), BB-42/43 add events and retargeting.
 */
export const FilterSpec = z.discriminatedUnion('type', [
  Comparison.extend({ type: z.literal('custom_attribute'), key: z.string().min(1).max(255), dataType: FilterValueType }),
  Comparison.extend({ type: z.literal('profile_field'), field: ProfileStringField }),
  /** Locale of the installation seen last. */
  Comparison.extend({ type: z.literal('device_locale') }),
  Comparison.extend({ type: z.literal('random_bucket') }),
  /** Sessions in total (all apps or one app); a profile without sessions has 0. */
  Comparison.extend({ type: z.literal('session_count'), appInstanceId: AppId.optional() }),
  Comparison.extend({ type: z.literal('sessions_in_last_days'), days: z.number().int().min(1).max(365), appInstanceId: AppId.optional() }),
  Comparison.extend({ type: z.literal('first_used_app'), appInstanceId: AppId.optional() }),
  Comparison.extend({ type: z.literal('last_used_app'), appInstanceId: AppId.optional() }),
  /** Most recent version of one app, compared as `major.minor.patch` (`1.10.0` > `1.9.3`). */
  z.strictObject({ type: z.literal('app_version'), appInstanceId: AppId, operator: z.enum(VERSION_OPERATORS), value: z.string().max(40).optional() }),
  /** Has (or had: uninstalls count) the app. */
  z.strictObject({ type: z.literal('has_app'), appInstanceId: AppId, operator: z.enum(['is_true', 'is_false']) }),
  Comparison.extend({ type: z.literal('device_os'), platform: DevicePlatform.optional(), deviceScope: DeviceScope.default('any_device') }),
  Comparison.extend({ type: z.literal('device_model'), deviceScope: DeviceScope.default('any_device') }),
  Comparison.extend({ type: z.literal('device_count') }),
  /** Channel state of push (BB-32); no record means `subscribed`. */
  z.strictObject({ type: z.literal('push_subscription'), operator: z.enum(['is_any_of']), value: z.array(z.enum(PUSH_SUBSCRIPTION_STATES)).min(1).max(3) }),
  /** A device that shows notifications (OS permission and a token, BB-31). */
  z.strictObject({ type: z.literal('push_enabled'), platform: DevicePlatform.optional(), operator: z.enum(['is_true', 'is_false']) }),
  /** State in a subscription group; no record means the group's default state (BB-32). */
  z.strictObject({ type: z.literal('subscription_group'), groupId: AppId, operator: z.enum(['equals']), value: z.enum(['subscribed', 'unsubscribed']) }),
  /** The profile's first paid install as AppsFlyer attributed it (BB-131); a profile without one has no value. */
  Comparison.extend({ type: z.literal('install_attribution'), field: z.enum(INSTALL_ATTRIBUTION_FIELDS) }),
  /** Membership of another segment; a segment used this way cannot itself use segment membership. */
  z.strictObject({ type: z.literal('segment_membership'), segmentId: AppId, operator: z.enum(['in', 'not_in']) }),
  /** Did / did not do an event, ever (BB-42). */
  z.strictObject({ type: z.literal('custom_event'), event: EventName, operator: z.enum(['did', 'did_not']) }),
  /** When the profile first / last did an event: a date, or more / less than N hours or days ago (0.25 h allowed). */
  z.strictObject({ type: z.literal('first_did_event'), event: EventName, operator: z.enum(EVENT_TIME_OPERATORS), value: z.union([z.string().max(40), z.number().positive()]), unit: z.enum(['hours', 'days']).optional() }),
  z.strictObject({ type: z.literal('last_did_event'), event: EventName, operator: z.enum(EVENT_TIME_OPERATORS), value: z.union([z.string().max(40), z.number().positive()]), unit: z.enum(['hours', 'days']).optional() }),
  /** How many times an event happened in a window. */
  z.strictObject({ type: z.literal('event_count_in_window'), event: EventName, operator: z.enum(EVENT_COUNT_OPERATORS), value: z.number().int().min(0), window: EventWindow }),
  /**
   * Retargeting (BB-43). "Received" is a channel-specific fact: push and other sends — accepted by the provider; Content
   * Cards — impression; in-app — impression. `withinDays` limits to the last N × 24 hours.
   */
  z.strictObject({ type: z.literal('received_message'), target: MessageTarget, operator: z.enum(['did', 'did_not']), withinDays: z.number().int().min(1).max(365).optional() }),
  z.strictObject({ type: z.literal('opened_message'), target: MessageTarget, operator: z.enum(['did', 'did_not']), withinDays: z.number().int().min(1).max(365).optional() }),
  z.strictObject({ type: z.literal('clicked_message'), target: MessageTarget, operator: z.enum(['did', 'did_not']), withinDays: z.number().int().min(1).max(365).optional() }),
  z.strictObject({ type: z.literal('has_never_received'), target: MessageTarget }),
  /** Converted from a campaign or Canvas; control group conversions do not count. */
  z.strictObject({ type: z.literal('converted_from'), target: CampaignOrCanvas, operator: z.enum(['did', 'did_not']) }),
  z.strictObject({ type: z.literal('in_control_group'), target: CampaignOrCanvas, operator: z.enum(['is_true', 'is_false']) }),
  z.strictObject({ type: z.literal('entered_canvas_variant'), canvasId: z.uuid(), variantApiId: z.string().min(1).max(120).optional(), operator: z.enum(['did', 'did_not']) }),
  /** Last message received (any channel or one channel) and last engagement (open or click on any channel). */
  z.strictObject({ type: z.literal('last_received_message'), channel: z.enum(MESSAGE_CHANNELS).optional(), operator: z.enum(EVENT_TIME_OPERATORS), value: z.union([z.string().max(40), z.number().positive()]), unit: z.enum(['hours', 'days']).optional() }),
  z.strictObject({ type: z.literal('last_engaged_message'), operator: z.enum(EVENT_TIME_OPERATORS), value: z.union([z.string().max(40), z.number().positive()]), unit: z.enum(['hours', 'days']).optional() }),
  /** How many times an event with a property condition happened in the last N days (segmentable properties only). */
  z.strictObject({
    type: z.literal('event_property_in_window'),
    event: EventName,
    property: z.strictObject({ path: z.string().min(1).max(255), dataType: FilterValueType, operator: z.string().min(1).max(40), value: Operand.optional(), value2: z.number().optional() }),
    operator: z.enum(EVENT_COUNT_OPERATORS),
    value: z.number().int().min(0),
    days: z.number().int().min(1).max(3650),
  }),
]);
export type FilterSpec = z.infer<typeof FilterSpec>;

const NodeId = z.string().min(1).max(64);

export type FilterNode =
  | { kind: 'and' | 'or'; id: string; label?: string | undefined; children: FilterNode[] }
  | { kind: 'not'; id: string; child: FilterNode }
  | { kind: 'filter'; id: string; filter: FilterSpec };

// Limits on depth and filter count are checked by the engine (`checkAudience`), which reports them with a path.
export const FilterNode: z.ZodType<FilterNode> = z.lazy(() =>
  z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('and'), id: NodeId, label: z.string().max(120).optional(), children: z.array(FilterNode).max(500) }),
    z.strictObject({ kind: z.literal('or'), id: NodeId, label: z.string().max(120).optional(), children: z.array(FilterNode).max(500) }),
    z.strictObject({ kind: z.literal('not'), id: NodeId, child: FilterNode }),
    z.strictObject({ kind: z.literal('filter'), id: NodeId, filter: FilterSpec }),
  ]),
);

export const AppsTargeting = z.discriminatedUnion('mode', [
  z.strictObject({ mode: z.literal('all') }),
  z.strictObject({ mode: z.literal('specific'), appInstanceIds: z.array(z.uuid()).min(1).max(50) }),
  z.strictObject({ mode: z.literal('none') }),
]);
export type AppsTargeting = z.infer<typeof AppsTargeting>;

/**
 * Who an audience includes (BB-40): profiles matching `include`, minus every `exclude` group (AND NOT), limited by the
 * apps they use. Stored as is by segments, campaigns, Canvas and cards.
 */
export const AudienceDefinition = z.strictObject({
  schemaVersion: z.literal(1),
  include: FilterNode,
  exclude: z.array(FilterNode).max(10),
  apps: AppsTargeting,
});
export type AudienceDefinition = z.infer<typeof AudienceDefinition>;

export const DescribeAudienceBody = z.strictObject({ definition: z.unknown(), locale: z.enum(['uk', 'en']).default('uk') });

/** Reach estimate and exact statistics (BB-44). The definition is checked like `describe`; an invalid one is a 400. */
export const EstimateAudienceBody = z.strictObject({ definition: z.unknown() });
export const REACH_BREAKDOWN = ['push_android', 'push_ios', 'card', 'iam', 'any'] as const;

export interface AudienceReach {
  /** `exact` for environments under the threshold and for exact statistics; `estimate` scales a stable sample. */
  mode: 'estimate' | 'exact';
  count: number;
  /** 95 % interval; equal to `count` when exact. */
  low: number;
  high: number;
  margin: number;
  population: number;
  percentOfEnvironment: number;
  /** Profiles reachable per channel (after suppression and global control); `any` — through at least one. */
  reachable: Record<(typeof REACH_BREAKDOWN)[number], number>;
  /** Profiles left after each top-level include group, in order. */
  funnel: Array<{ id: string; label?: string; count: number }>;
  sample: { buckets: number; profiles: number } | null;
  computedAt: string;
  dataWatermark: string;
  cacheHit: boolean;
}

export const EXACT_STATUSES = ['queued', 'running', 'completed', 'cancelled', 'failed'] as const;
export interface AudienceExactRequest {
  id: string;
  status: (typeof EXACT_STATUSES)[number];
  result: AudienceReach | null;
  /** The data changed since the result was computed. */
  stale: boolean;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

/** User Lookup (BB-45): is one profile in an audience, and why not. Email and phone need `pii.view`. */
export const LOOKUP_BY = ['external_id', 'profile_id', 'email', 'phone'] as const;
export const LookupAudienceBody = z.strictObject({
  definition: z.unknown(),
  profile: z.strictObject({ by: z.enum(LOOKUP_BY), value: z.string().trim().min(1).max(320) }),
  locale: z.enum(['uk', 'en']).default('en'),
});
export type LookupAudienceBody = z.infer<typeof LookupAudienceBody>;

export interface ExplainedNode {
  nodeId: string;
  kind: 'and' | 'or' | 'not' | 'filter';
  label?: string;
  /** For an exclusion group, `true` means it removed the profile. */
  result: boolean;
  sentence: string;
  /** The profile's value the filter compared; `null` when it has none. */
  actual?: unknown;
  /** The value is personal data the reader may not see. */
  actualMasked?: boolean;
  /** The operand of the filter (`value`, and `value2` of `between`). */
  expected?: unknown;
  children?: ExplainedNode[];
}

export interface AudienceBlocker {
  nodeId: string | null;
  reason: 'not_matched' | 'excluded' | 'apps' | 'inactive';
  sentence: string;
  actual?: unknown;
  actualMasked?: boolean;
}

export interface AudienceLookup {
  profile: { id: string; externalId: string | null; status: string; /** The looked-up profile was merged into `id`. */ mergedFrom: string | null };
  match: boolean;
  include: ExplainedNode;
  exclude: ExplainedNode[];
  apps: { mode: AppsTargeting['mode']; match: boolean };
  blockers: AudienceBlocker[];
  /** Profiles never messaged whatever the audience (suppression lists, global control group — BB-49). */
  exclusions: Array<{ kind: 'suppression' | 'global_control'; reason: string; listId?: string }>;
  evaluatedAt: string;
}
